import React from 'react';
import { Button } from '@/components/ui/button';
import { Edit, Eye } from 'lucide-react';

interface DataTableCardViewProps {
  data: any[];
  columns: any[];
  loading?: boolean;
  error?: string | null;
  onRowClick?: (row: any) => void;
  userIdToName?: Record<string, string>;
  pageSize?: number;
  pageIndex?: number;
  pageCount?: number;
  onPageChange?: (pageIndex: number) => void;
}

export const DataTableCardView: React.FC<DataTableCardViewProps> = ({ data, columns, loading, error, onRowClick, userIdToName = {}, pageSize = 10, pageIndex = 0, pageCount, onPageChange }) => {
  if (loading) {
    return <div className="py-8 text-center">Loading...</div>;
  }
  if (error) {
    return <div className="py-8 text-center text-destructive">{error}</div>;
  }
  if (!data || data.length === 0) {
    return <div className="py-8 text-center text-muted-foreground">No data found.</div>;
  }
  
  // Implement pagination for mobile view
  const startIndex = pageIndex * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedData = data.slice(startIndex, endIndex);
  
  // Calculate pageCount if not provided
  const calculatedPageCount = pageCount !== undefined ? pageCount : Math.ceil(data.length / pageSize);
  
  return (
    <div className="flex flex-col gap-4 w-full">
      {paginatedData.map((row, i) => (
        <div
          key={row.id || i}
          className="rounded-lg border bg-card p-4 shadow-sm hover:shadow-md transition cursor-pointer"
          onClick={() => onRowClick?.(row)}
        >
          {columns.map((col) => {
            // Special handling for action columns
            if (col.meta?.isAction) {
              const label = (col.meta && col.meta.cardLabel) || 'Actions';
              return (
                <div key={col.id || col.accessorKey} className="mb-2">
                  <div className="text-xs font-semibold text-muted-foreground uppercase mb-1">{label}</div>
                  <div className="text-base text-foreground break-words">
                    {typeof col.cell === 'function' ? col.cell({ row: { original: row } }) : (
                      <Button variant="ghost" size="icon">
                        <Edit className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              );
            }
            
            let value;
            if (col.accessorKey) {
              value = row[col.accessorKey];
            } else if (typeof col.cell === 'function') {
              // Emulate TanStack's cell context: { row: { original: row } }
              value = col.cell({ row: { original: row } });
            } else {
              value = '';
            }
            
            // Special handling for owner column to convert user ID to name
            if (col.accessorKey === 'owner' && typeof value === 'string' && userIdToName[value]) {
              value = userIdToName[value];
            }
            
            // Prefer explicit mobile label from column meta; fall back to a safe text key
            const label = (col.meta && col.meta.cardLabel)
              || (typeof col.header === 'string' ? col.header : (col.id || col.accessorKey || ''));
            return (
              <div key={col.id || col.accessorKey} className="mb-2">
                <div className="text-xs font-semibold text-muted-foreground uppercase mb-1">{label}</div>
                <div className="text-base text-foreground break-words">
                  {(() => {
                    if (value instanceof Date) {
                      return value.toLocaleString();
                    }
                    if (React.isValidElement(value)) {
                      return value;
                    }
                    if (Array.isArray(value)) {
                      return value.map((v, i) =>
                        typeof v === 'object' && v !== null
                          ? '[object]'
                          : String(v)
                      ).join(', ');
                    }
                    if (typeof value === 'object' && value !== null) {
                      // Prevent circular structure and React/DOM node rendering
                      return '[object]';
                    }
                    if (typeof value === 'function') {
                      return '';
                    }
                    return value;
                  })()}

                </div>
              </div>
            );
          })}
        </div>
      ))}
      
      {/* Pagination Controls */}
      <div className="flex items-center justify-between px-2 py-4">
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange && onPageChange(Math.max(0, pageIndex - 1))}
            disabled={pageIndex === 0}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange && onPageChange(Math.min(calculatedPageCount - 1, pageIndex + 1))}
            disabled={pageIndex >= calculatedPageCount - 1}
          >
            Next
          </Button>
        </div>
        <div className="text-sm text-muted-foreground">
          Page {pageIndex + 1} of {calculatedPageCount}
        </div>
      </div>
    </div>
  );
};
